import { useScroll } from "framer-motion";
import { useRef, useState, useEffect, type FC } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { type EmblaCarouselType } from "embla-carousel";
import { Heading } from "@tennr/frontend/components/Heading/Heading";
import { Testimonial } from "@tennr/frontend/components/Testimonial/Testimonial";
import { doubleQuote, singleQuote } from "@tennr/frontend/utils";
import "./BookTestimonialsSection.css";

export const BookTestimonialsSection: FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  const [emblaRef, emblaApi] = useEmblaCarousel();
  const [, setSlidesInView] = useState<number[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollTo = (index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  };

  useEffect(() => {
    if (!emblaApi) return;

    const updateSlidesInView = (emblaApi: EmblaCarouselType): void => {
      setSlidesInView(emblaApi.slidesInView());
      setSelectedIndex(emblaApi.selectedScrollSnap()); // 👈 Add this
    };

    updateSlidesInView(emblaApi);
    emblaApi.on("slidesInView", updateSlidesInView);
    emblaApi.on("reInit", updateSlidesInView);
    emblaApi.on("select", updateSlidesInView); // 👈 Also listen for slide selection
  }, [emblaApi]);

  // add standard Framer Motion scroll tracking for opacity animation
  useScroll({
    target: sectionRef,
    offset: ["start 100vh", "start start"],
  });

  // transform scroll progress to opacity values:
  // 0 to 0.5 scrollYProgress => 0 to 1 opacity
  // 0.5 to 1 scrollYProgress => 1 to 0 opacity

  const testimonials = [
    {
      quote: doubleQuote(
        "With Tennr, we’re able to do things so much faster, and it's just huge. It’s been a game changer. We’re able to reach out to patients so much faster."
      ),
      image: "/img/testimonials/alyssa-jakubowski.jpeg",
      attributedTo: "Alyssa Jakubowski",
      attributedToTitle: "Revenue Cycle and Compliance Manager at NEB Medical",
    },
    {
      quote: doubleQuote(
        `I call it the ${singleQuote("Tennr effect.")} For every action, there's a reaction. As we speed things up at the front of the time, it's going to have downstream effects.`
      ),
      image: "/img/testimonials/chip-holland.jpeg",
      attributedTo: "Chip Holland",
      attributedToTitle: "President of Total Sleep Management",
    },
    {
      quote: doubleQuote("We hit 260 faxes in one day… and were able to manage it."),
      image: "/img/testimonials/amy-willis.jpeg",
      attributedTo: "Amy Willis",
      attributedToTitle: "Director of Complex Rehab at King Drug",
    },
  ];

  return (
    <section className="testimonials-section" ref={sectionRef} data-tracker-id="testimonials-section">
      <Heading
        title="Testimonials"
        heading="Our customers know best"
        body="And their teams aren't buried in admin."
        color="dark"
        variant="section"
        width={560}
        wrapHeading="balance"
      />
      <div className="testimonials-carousel" ref={emblaRef}>
        <div className="testimonials-carousel-slides">
          {testimonials.map(({ quote, image, attributedTo, attributedToTitle }, i) => {
            return (
              <div
                key={i}
                className="testimonials-carousel-slide"
                onClick={() => scrollTo(i)}
              >
                <Testimonial
                  key={i}
                  quote={quote}
                  image={image}
                  attributedTo={attributedTo}
                  attributedToTitle={attributedToTitle}
                />
              </div>
            );
          })}
        </div>
      </div>
      <div className="carousel-dots-left">
        {testimonials.map((_, index) => (
          <button
            key={index}
            className={`carousel-dot ${selectedIndex === index ? "active" : ""}`}
            onClick={() => scrollTo(index)}
          />
        ))}
      </div>
    </section>
  );
};
